import React, { useState, useRef, useEffect } from 'react';
import api from '../api';

const RedactModal = ({ hoaCode, sourceDocId, grantId, defaultDays = 7, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rects, setRects] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  // Set canvas size to match container with proper devicePixelRatio
  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas || !containerRef.current) return;

    const resize = () => {
      const box = canvas.getBoundingClientRect();
      // set backing store size (device pixels)
      canvas.width = Math.floor(box.width * (window.devicePixelRatio || 1));
      canvas.height = Math.floor(box.height * (window.devicePixelRatio || 1));
      // scale context so 1 CSS pixel = 1 canvas unit
      const ctx = canvas.getContext('2d');
      ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);
      // re-draw existing rects if any
      rects.forEach(r => {
        ctx.fillStyle = 'rgba(0,0,0,0.8)';
        ctx.fillRect(r.x, r.y, r.width, r.height);
      });
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [rects]);

  const handleMouseDown = (e) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    
    const rect = overlay.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setDrawing({ startX: x, startY: y });
  };

  const handleMouseMove = (e) => {
    if (!drawing || !overlayRef.current) return;

    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = overlayRef.current.getContext('2d');
    ctx.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    
    // Re-draw existing rects
    rects.forEach(r => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(r.x, r.y, r.width, r.height);
    });
    
    // Draw current rect being drawn
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(drawing.startX, drawing.startY, x - drawing.startX, y - drawing.startY);
  };

  const handleMouseUp = (e) => {
    if (!drawing || !overlayRef.current) return;

    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const width = x - drawing.startX;
    const height = y - drawing.startY;

    if (Math.abs(width) > 10 && Math.abs(height) > 10) {
      const newRect = {
        x: Math.min(drawing.startX, x),
        y: Math.min(drawing.startY, y),
        width: Math.abs(width),
        height: Math.abs(height)
      };

      const newRects = [...rects, newRect];
      setRects(newRects);
      
      const ctx = overlayRef.current.getContext('2d');
      newRects.forEach(r => {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(r.x, r.y, r.width, r.height);
      });
    }

    setDrawing(null);
  };

  const handleSaveRedacted = async () => {
    if (rects.length === 0) {
      alert('Please draw at least one redaction rectangle');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/basic-redaction/create', {
        source_doc_id: sourceDocId,
        rects: rects,
        visible_to: 'public'
      });

      alert('✅ Redaction saved!');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('Error saving redaction:', err);
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setRects([]);
    if (overlayRef.current) {
      const ctx = overlayRef.current.getContext('2d');
      ctx.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-[95vw] h-[95vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">🔒 Simple Redaction - NO CONVERSION</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-900">
            <strong>✨ Click and drag with your mouse</strong> to draw black rectangles over sensitive information. No PDF conversion!
          </p>
        </div>

        <div className="flex-1 relative border-2 border-gray-300 rounded-lg overflow-auto bg-gray-100">
          <div
            ref={containerRef}
            className="relative w-full h-full"
          >
            <iframe
              src={`/api/documents/${sourceDocId}/view#toolbar=0&navpanes=0`}
              className="w-full h-full absolute top-0 left-0"
              title="Document Redaction"
            />
            <canvas
              ref={overlayRef}
              className="absolute top-0 left-0 cursor-crosshair"
              style={{ 
                width: '100%', 
                height: '100%',
                pointerEvents: 'auto',
                touchAction: 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            />
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <div className="flex-1 text-sm text-gray-600">{rects.length} redaction(s) drawn - Click and drag to add more</div>
          <button onClick={handleClearAll} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Clear All</button>
          <button onClick={handleSaveRedacted} disabled={rects.length === 0 || loading} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400">
            {loading ? 'Saving...' : 'Save Redaction'}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default RedactModal;
